import { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Plan, useBatchUploadQuery, useCreateBatchPaymentMutation, useUserQuery } from '../../../../api/gql';
import { BillingEnterpriseBox } from '../../../../components/BillingEnterpriseBox';
import { BillingPlans } from '../../../../components/BillingPlans';
import { FullSizeLoader } from '../../../../components/FullSizeLoader';
import { PageHeader } from '../../../../components/PageHeader';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';
import { PageContent, PageLayout } from '../../../../layout';
import { PaymentModal } from '../../../../modules/payment/PaymentModal';
import { notify } from '../../../../utils/notification';
import { fromCentesimal } from '../../../../utils/transform';

const TRANS_NS = 'billing';

export const BatchBilling = () => {
  const { batchId } = useParams();
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();
  const [clientPaymentSecret, setClientPaymentSecret] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const handleQueryError = (error: unknown) => {
    console.error(error);
    notify.negative({ message: t('query.error') });
    safeNavigate(-1);
  };

  const handleMutationError = (error: unknown) => {
    console.error(error);
    notify.negative({ message: t('mutation.error') });
  };

  const { data: userData, isLoading: isLoadingUserQuery, error } = useUserQuery();

  const {
    data: batchData,
    isLoading: isLoadingBatchQuery,
    error: batchQueryError,
  } = useBatchUploadQuery({
    id: batchId ?? '',
  });

  useEffect(() => {
    if (error || batchQueryError) {
      console.error(error);
      handleQueryError(error || batchQueryError);
    }
  }, [batchQueryError, error, handleQueryError]);

  const { mutate: createPaymentMutate, isPending: isMutating } = useCreateBatchPaymentMutation({
    onSuccess: ({ createBatchPayment: { paymentRequired, clientSecret } }) =>
      handleSuccess(paymentRequired, clientSecret),
    onError: handleMutationError,
  });

  const handleSuccess = (paymentRequired: boolean, clientSecret?: string | null) => {
    if (paymentRequired && clientSecret) {
      setClientPaymentSecret(clientSecret);
      setPaymentModalOpen(true);
      return;
    }
    notify.positive({ message: t('mutation.success') });
    safeNavigate(`/`, { replace: true });
  };

  const onPlanChoose = (plan: Plan) => {
    if (!batchId) {
      throw new Error('');
    }
    if (clientPaymentSecret) {
      setPaymentModalOpen(true);
      return;
    }
    createPaymentMutate({ id: batchId, plan: plan });
  };

  const isLoading = useMemo(() => isLoadingBatchQuery || isLoadingUserQuery, [isLoadingUserQuery, isLoadingBatchQuery]);

  return (
    <PageLayout>
      <PageHeader
        title={t('title')}
        subtitle={userData?.user.isEnterprise ? t('enterprise.subtitle') : t('subtitle')}
      />
      <PageContent
        sx={{
          px: { md: 4, xs: 0 },
          pt: { xs: 4, md: 8 },
          alignItems: 'center',
        }}
      >
        {isLoading ? <FullSizeLoader /> : null}

        {batchData && !userData?.user.isEnterprise ? (
          <BillingPlans
            scenarioPlan={batchData.batchUpload.scenario?.plan}
            onPlanChoose={onPlanChoose}
            standardDiscount={fromCentesimal(batchData.batchUpload.standardDiscount ?? 0)}
            standardPrice={fromCentesimal(batchData.batchUpload.standardPrice ?? 0)}
            isFreePlanDisplayed={false}
          />
        ) : null}
        {userData?.user.isEnterprise ? (
          <Box display={'flex'} flex={'1 1 0'} alignItems={'flex-start'} justifyContent={'center'} height={'100%'}>
            <BillingEnterpriseBox handleSubmit={() => onPlanChoose(Plan.Standard)} isLoading={isMutating} />
          </Box>
        ) : null}
      </PageContent>

      <PaymentModal
        returnUrl={`${window.location.origin}/upload/batch/${batchId ?? ''}/payment-result`}
        clientSecret={clientPaymentSecret}
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
      />
    </PageLayout>
  );
};
